import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';

import { Alert, Box, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';

import { useGetCarouselMetrics } from '~/hooks/apis/experiences/useGetCarouselMetrics';

import {
  CarouselMetric,
  GeoFilter,
  OnDemandCarouselMetric,
  getCarouselMetrics,
  getGeoValues,
} from '~/services/ExperiencesService';
import { Dayjs, getDayJs } from '~/services/TimeService';
import { customerPortalHost } from '~/services/common';

import HomepageMetricsFilters, { CountryOption } from './HomepageMetricsFilters';
import HomepageMetricsTable from './HomepageMetricsTable';
import { HomepageMetricsDisplayRow, HomepageMetricsSortDirection, HomepageMetricsSortField } from './types';

const CAROUSEL_DISPLAY_NAMES: Record<string, string> = {
  'OfferList - EXPERIENCES - Recently Viewed Carousel': 'Recently Viewed',
  'OfferList - EXPERIENCES - Experiences Top Three Carousel': 'Our top 3 experiences',
  'OfferList - EXPERIENCES - YAML Carousel': 'Local discounts exclusive to LuxPlus+ members',
  'OfferList - EXPERIENCES - Experiences Our exclusive experiences Carousel': 'Our exclusive experiences',
  'OfferList - EXPERIENCES - Experiences Top activities for you Carousel': 'Top activities for you',
  'OfferList - EXPERIENCES - Experiences Discover dining experiences Carousel': 'Discover dining experiences',
  'OfferList - EXPERIENCES - Experiences Explore your destination Carousel': 'Explore your destination',
  'OfferList - EXPERIENCES - Collections Carousel - Summer Highlights': 'Summer Highlights',
  'OfferList - EXPERIENCES - Collections Carousel - Things to Do with Kids': 'Things to Do with Kids',
  'OfferList - EXPERIENCES - Collections Carousel - Romantic Experiences': 'Romantic Experiences',
  'OfferList - EXPERIENCES - Collections Carousel - Perfect Gifts for Someone Special':
    'Perfect Gifts for Someone Special',
  'OfferList - EXPERIENCES - Experiences See the sights Carousel': 'See the sights',
  'OfferList - EXPERIENCES - Experiences Live events Carousel': 'Live events',
  'OfferList - EXPERIENCES - Experiences Indulge yourself Carousel': 'Indulge yourself',
};

const CP_CAROUSEL_ORDER = [
  'OfferList - EXPERIENCES - Recently Viewed Carousel',
  'OfferList - EXPERIENCES - Experiences Top Three Carousel',
  'OfferList - EXPERIENCES - YAML Carousel',
  'OfferList - EXPERIENCES - Experiences Our exclusive experiences Carousel',
  'OfferList - EXPERIENCES - Experiences Top activities for you Carousel',
  'OfferList - EXPERIENCES - Experiences Discover dining experiences Carousel',
  'OfferList - EXPERIENCES - Experiences Explore your destination Carousel',
  'OfferList - EXPERIENCES - Collections Carousel - Summer Highlights',
  'OfferList - EXPERIENCES - Collections Carousel - Things to Do with Kids',
  'OfferList - EXPERIENCES - Collections Carousel - Romantic Experiences',
  'OfferList - EXPERIENCES - Collections Carousel - Perfect Gifts for Someone Special',
  'OfferList - EXPERIENCES - Experiences See the sights Carousel',
  'OfferList - EXPERIENCES - Experiences Live events Carousel',
  'OfferList - EXPERIENCES - Experiences Indulge yourself Carousel',
];

export function getDisplayName(carouselName: string): string {
  return CAROUSEL_DISPLAY_NAMES[carouselName] ?? carouselName;
}

export function getCpOrder(carouselName: string): number {
  const index = CP_CAROUSEL_ORDER.indexOf(carouselName);
  return index === -1 ? CP_CAROUSEL_ORDER.length : index;
}

type CustomMetricsState = {
  loading: boolean;
  data: Array<OnDemandCarouselMetric> | null;
  error: string | null;
};

export function toDisplayRow(metric: CarouselMetric): HomepageMetricsDisplayRow {
  const m = metric.metrics7d?.last7d;
  return {
    sectionId: metric.carouselName,
    displayName: getDisplayName(metric.carouselName),
    cpOrder: getCpOrder(metric.carouselName),
    views: m?.sessionsWithViews ?? 0,
    clicks: m?.clicks ?? 0,
    orders: m?.orders ?? 0,
    grossSalesAud: m?.grossSalesAud ?? 0,
    marginAud: m?.marginAud ?? 0,
    daysWithViews: m?.daysWithViews ?? 0,
  };
}

export function toDisplayRowFromOnDemand(metric: OnDemandCarouselMetric): HomepageMetricsDisplayRow {
  return {
    sectionId: metric.carouselName,
    displayName: getDisplayName(metric.carouselName),
    cpOrder: getCpOrder(metric.carouselName),
    views: metric.metrics.sessionsWithViews,
    clicks: metric.metrics.clicks,
    orders: metric.metrics.orders,
    grossSalesAud: metric.metrics.grossSalesAud,
    marginAud: metric.metrics.marginAud,
    daysWithViews: metric.metrics.daysWithViews,
  };
}

export function getSortValue(row: HomepageMetricsDisplayRow, field: HomepageMetricsSortField): number | string {
  switch (field) {
    case 'cpOrder':
      return row.cpOrder;
    case 'displayName':
      return row.displayName;
    case 'views':
      return row.views;
    case 'clicks':
      return row.clicks;
    case 'orders':
      return row.orders ?? 0;
    case 'grossSales':
      return row.grossSalesAud ?? 0;
    case 'margin':
      return row.marginAud ?? 0;
    default:
      return 0;
  }
}

export default function LegacyHomepageMetrics() {
  const { data: metrics = [], isLoading, error } = useGetCarouselMetrics();
  const tenant = useSelector((state: App.State) => state.tenant);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<HomepageMetricsSortField>('cpOrder');
  const [sortDirection, setSortDirection] = useState<HomepageMetricsSortDirection>('asc');
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([null, null]);
  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(null);
  const [geoFilter, setGeoFilter] = useState<GeoFilter>({});
  const [customMetrics, setCustomMetrics] = useState<CustomMetricsState | null>(null);
  const [regionOptions, setRegionOptions] = useState<Array<string>>([]);
  const [cityOptions, setCityOptions] = useState<Array<string>>([]);
  const [regionLoading, setRegionLoading] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);

  const fetchRequestRef = useRef(0);

  const debouncedFetch = useMemo(
    () =>
      debounce(async (startDate: string, endDate: string, geo: GeoFilter) => {
        const requestId = ++fetchRequestRef.current;
        setCustomMetrics((prev) => ({ loading: true, data: prev?.data ?? null, error: null }));

        try {
          const results = await getCarouselMetrics(startDate, endDate, geo);
          if (fetchRequestRef.current !== requestId) {
            return;
          }
          setCustomMetrics({ loading: false, data: results, error: null });
        } catch {
          if (fetchRequestRef.current !== requestId) {
            return;
          }
          setCustomMetrics({
            loading: false,
            data: null,
            error: 'Failed to fetch custom range metrics',
          });
        }
      }, 800),
    [],
  );

  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  const triggerFetch = useCallback(
    (range: DateRange<Dayjs>, geo: GeoFilter) => {
      const [start, end] = range;
      const hasGeo = !!(geo.country || geo.region || geo.city);
      const hasValidRange = start && end && start.isValid() && end.isValid() && !end.isBefore(start);

      if (!hasValidRange && !hasGeo) {
        debouncedFetch.cancel();
        fetchRequestRef.current++;
        setCustomMetrics(null);
        return;
      }

      const today = getDayJs();
      const effectiveStart = hasValidRange ? start.format('YYYY-MM-DD') : today.subtract(7, 'day').format('YYYY-MM-DD');
      const effectiveEnd = hasValidRange ? end.format('YYYY-MM-DD') : today.subtract(1, 'day').format('YYYY-MM-DD');
      debouncedFetch(effectiveStart, effectiveEnd, geo);
    },
    [debouncedFetch],
  );

  const handleDateRangeChange = useCallback(
    (newRange: DateRange<Dayjs>) => {
      setDateRange(newRange);
      triggerFetch(newRange, geoFilter);
    },
    [triggerFetch, geoFilter],
  );

  const handleCountryChange = useCallback(
    (_: unknown, value: CountryOption | null) => {
      setSelectedCountry(value);
      const nextGeo: GeoFilter = { country: value?.iso2 };
      setGeoFilter(nextGeo);
      setRegionOptions([]);
      setCityOptions([]);
      triggerFetch(dateRange, nextGeo);

      if (value?.iso2) {
        setRegionLoading(true);
        getGeoValues(value.iso2, 'region')
          .then(setRegionOptions)
          .catch(() => setRegionOptions([]))
          .finally(() => setRegionLoading(false));
      }
    },
    [triggerFetch, dateRange],
  );

  const handleRegionChange = useCallback(
    (_: unknown, value: string | null) => {
      const nextGeo = { ...geoFilter, region: value || undefined, city: undefined };
      setGeoFilter(nextGeo);
      setCityOptions([]);
      triggerFetch(dateRange, nextGeo);

      if (selectedCountry?.iso2 && value) {
        setCityLoading(true);
        getGeoValues(selectedCountry.iso2, 'city', value)
          .then(setCityOptions)
          .catch(() => setCityOptions([]))
          .finally(() => setCityLoading(false));
      }
    },
    [triggerFetch, dateRange, geoFilter, selectedCountry],
  );

  const handleCityChange = useCallback(
    (_: unknown, value: string | null) => {
      const nextGeo = { ...geoFilter, city: value || undefined };
      setGeoFilter(nextGeo);
      triggerFetch(dateRange, nextGeo);
    },
    [triggerFetch, dateRange, geoFilter],
  );

  const handleSort = (field: HomepageMetricsSortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const isCustomRange = customMetrics?.data != null;

  const displayRows: Array<HomepageMetricsDisplayRow> = useMemo(() => {
    if (customMetrics?.data) {
      return customMetrics.data.map(toDisplayRowFromOnDemand);
    }
    return metrics.map(toDisplayRow);
  }, [customMetrics?.data, metrics]);

  const filtered = useMemo(
    () =>
      displayRows.filter(
        (row) =>
          !searchTerm.trim() ||
          row.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          row.sectionId.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [displayRows, searchTerm],
  );

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aVal = getSortValue(a, sortField);
      const bVal = getSortValue(b, sortField);
      const cmp =
        typeof aVal === 'string'
          ? aVal.localeCompare(typeof bVal === 'string' ? bVal : String(bVal))
          : aVal - (typeof bVal === 'number' ? bVal : 0);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortField, sortDirection]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">{error instanceof Error ? error.message : 'Failed to fetch carousel metrics'}</Alert>
    );
  }

  if (metrics.length === 0 && !customMetrics?.data) {
    return <Alert severity="info">No carousel metrics available yet. Data syncs daily at 6am Sydney.</Alert>;
  }

  const updatedAt = metrics[0]?.updatedAt
    ? new Date(metrics[0].updatedAt).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })
    : 'N/A';

  const geoLabels = [selectedCountry?.label, geoFilter.region, geoFilter.city].filter(Boolean).join(' | ');
  const hasExplicitRange = dateRange[0] && dateRange[1];
  const rangeLabel = isCustomRange
    ? `${hasExplicitRange ? 'Custom Range' : 'Last 7 Days'}${geoLabels ? ` | ${geoLabels}` : ''}`
    : 'Last 7 Days (cached)';

  return (
    <Stack direction="column" spacing={3}>
      <HomepageMetricsFilters
        dateRange={dateRange}
        selectedCountry={selectedCountry}
        geoFilter={geoFilter}
        regionOptions={regionOptions}
        cityOptions={cityOptions}
        regionLoading={regionLoading}
        cityLoading={cityLoading}
        onDateRangeChange={handleDateRangeChange}
        onCountryChange={handleCountryChange}
        onRegionChange={handleRegionChange}
        onCityChange={handleCityChange}
      />
      {customMetrics?.loading && <CircularProgress size={20} />}

      {customMetrics?.error && <Alert severity="error">{customMetrics.error}</Alert>}

      <Stack direction="row" spacing={3} alignItems="center">
        <Box maxWidth={400} flexGrow={1}>
          <TextField
            fullWidth
            label="Search carousels"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {sorted.length} carousel{sorted.length !== 1 ? 's' : ''} | {rangeLabel}
          {!isCustomRange && ` | Last sync: ${updatedAt}`}
        </Typography>
      </Stack>

      <HomepageMetricsTable
        rows={sorted}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        getLink={(row) => `${customerPortalHost(tenant)}/experiences?carousel=${encodeURIComponent(row.sectionId)}`}
      />
    </Stack>
  );
}
